import type { CodeList } from "../genericode";
import {
  assert,
  type BasicData,
  type Child,
  type Field,
  type FieldConstraints,
  type FieldImport,
  type Group,
  type GroupImport,
  type Rule,
  type RuleReference,
  type RuleVersionReference,
  type Schema,
  type SchemaDocument,
  type VersionChild,
} from "../project";
import {
  DEFAULT_DOCUMENT_PROFILE,
  FreigabeStatus,
  type xdf2,
} from "../xdatenfelder";

export function createSchemaDocument(
  message: xdf2.SchemaMessage,
  codeLists: Record<string, CodeList>,
): SchemaDocument {
  const container = message.schemaContainer;

  const schema = convertSchema(container.schema);
  if (schema.type !== "version") {
    throw new Error("Expected versioned schema");
  }

  const groups: Record<string, Group> = {};
  for (const xdfGroup of Object.values(container.datenfeldgruppen.entries())) {
    const group = convertGroup(xdfGroup);
    groups[group.fimId] = group;
  }

  const fields: Record<string, Field> = {};
  for (const xdfField of Object.values(container.datenfelder.entries())) {
    const field = convertField(xdfField, codeLists);
    fields[field.fimId] = field;
  }

  const rules: Record<string, Rule> = {};
  for (const xdfRule of Object.values(container.regeln.entries())) {
    const rule = convertRule(xdfRule);
    rules[rule.fimId] = rule;
  }

  return {
    type: "schema",
    schema,
    groups,
    fields,
    rules,
  };
}

export function createGroupImport(
  message: xdf2.GroupMessage,
  codeLists: Record<string, CodeList>,
): GroupImport {
  const [fimId, fimVersion] = upgradeIdentifier(message.root);

  const groups: Group[] = [];
  for (const xdfGroup of Object.values(message.groups.entries())) {
    const group = convertGroup(xdfGroup);
    groups.push(group);
  }

  const fields: Field[] = [];
  for (const xdfField of Object.values(message.fields.entries())) {
    const field = convertField(xdfField, codeLists);
    fields.push(field);
  }

  const rules: Rule[] = [];
  for (const xdfRule of Object.values(message.rules.entries())) {
    const rule = convertRule(xdfRule);
    rules.push(rule);
  }

  return {
    rootGroupIdentifier: {
      fimId,
      fimVersion,
    },
    groups,
    fields,
    rules,
  };
}

export function createFieldImport(
  message: xdf2.FieldMessage,
  codeLists: Record<string, CodeList>,
): FieldImport {
  const field = convertField(message.field, codeLists);

  const rules: Rule[] = [];
  for (const xdfRule of Object.values(message.regeln.entries())) {
    const rule = convertRule(xdfRule);
    rules.push(rule);
  }

  return {
    field,
    rules,
  };
}

function convertRule(rule: xdf2.Regel): Rule {
  const [fimId, fimVersion] = upgradeIdentifier(rule.identifier);

  if (fimVersion === undefined) {
    return {
      type: "arbeitskopie",
      fimId,
      name: rule.name,
      beschreibung: rule.beschreibung,
      freitextRegel: rule.definition,
      bezug: rule.bezug ? [{ value: rule.bezug }] : [],
      stichwort: [],
      fachlicherErsteller: rule.fachlicherErsteller,
      letzteAenderung: undefined,
      ruleType: "valid",
      skript: rule.script,
      expression: undefined,
      target: undefined,
    };
  } else {
    return {
      type: "version",
      fimId,
      fimVersion,
      name: rule.name,
      beschreibung: rule.beschreibung,
      freitextRegel: rule.definition,
      bezug: rule.bezug ? [{ value: rule.bezug }] : [],
      stichwort: [],
      fachlicherErsteller: rule.fachlicherErsteller,
      letzteAenderung: undefined,
      ruleType: "valid",
      skript: rule.script,
    };
  }
}

function convertRuleReferences(references: string[]): RuleReference[] {
  return references.map((ref) => {
    const [fimId, fimVersion] = upgradeIdentifier(ref);
    return { fimId, fimVersion };
  });
}

function convertRuleVersionReferences(
  references: string[],
): RuleVersionReference[] {
  return references.map((ref) => {
    const [fimId, fimVersion] = upgradeIdentifier(ref);
    if (fimVersion === undefined) {
      throw new Error(`Expected versioned rule reference: ${ref}`);
    }

    return { fimId, fimVersion };
  });
}

function convertField(
  field: xdf2.Datenfeld,
  codeLists: Record<string, CodeList>,
): Field {
  const [fimId, fimVersion] = upgradeIdentifier(field.identifier);

  const options: [string, string][] = [];
  if (field.codelisteReferenz !== undefined) {
    const genericodeIdentifier =
      field.codelisteReferenz.genericode.canonicalVersionUri;
    const codeList = codeLists[genericodeIdentifier];
    if (codeList === undefined) {
      console.warn(`Code list not found: ${genericodeIdentifier}`);
    } else {
      try {
        const keys = codeList.getKeyColumn();
        const values = codeList.selectNameColumn();

        assert(keys.length === values.length, "Invalid code list");
        for (let i = 0; i < keys.length; i++) {
          options.push([keys[i], values[i]]);
        }
      } catch (e) {
        console.error(
          `Error while converting code list: ${genericodeIdentifier}: ${e}`,
        );
      }
    }
  }

  if (fimVersion === undefined) {
    return {
      type: "arbeitskopie",
      fimId,
      basicData: convertBaseData(field),
      inputType: field.feldart,
      dataType: field.datentyp,
      value: field.inhalt,
      rules: convertRuleReferences(field.regeln),
      options,
      constraints: convertPrarezisierung(field.praezisierung),
    };
  } else {
    return {
      type: "version",
      url: `https://fimportal.de/fields/baukasten/${field.id}/${field.version}`,
      namespace: "baukausten",
      fimId,
      fimVersion,
      basicData: convertBaseData(field),
      inputType: field.feldart,
      dataType: field.datentyp,
      value: field.inhalt,
      rules: convertRuleVersionReferences(field.regeln),
      options,
      constraints: convertPrarezisierung(field.praezisierung),
    };
  }
}

function convertPrarezisierung(_value: string | undefined): FieldConstraints {
  // TODO: implement
  return {
    minLength: undefined,
    maxLength: undefined,
    minValue: undefined,
    maxValue: undefined,
    pattern: undefined,
  };
}

function convertSchema(schema: xdf2.Stammdatenschema): Schema {
  const [fimId, fimVersion] = upgradeIdentifier(schema.identifier);

  if (fimVersion === undefined) {
    return {
      type: "arbeitskopie",
      fimId,
      bezeichnung: schema.bezeichnungEingabe,
      hilfeText: schema.hilfetext,
      ableitungsModifikationenRepraesentation:
        schema.ableitungsmodifikationenRepraesentation,
      ableitungsModifikationenStrutkur: schema.ableitungsmodifikationenStruktur,
      documentSteckbriefFimId: DEFAULT_DOCUMENT_PROFILE,
      basicData: convertBaseData(schema),
      children: convertChildren(schema.elemente),
      rules: convertRuleReferences(schema.regeln),
    };
  } else {
    return {
      type: "version",
      url: `https://fimportal.de/schemas/${schema.id}/${schema.version}`,
      fimId,
      fimVersion,
      bezeichnung: schema.bezeichnungEingabe,
      hilfeText: schema.hilfetext,
      ableitungsModifikationenRepraesentation:
        schema.ableitungsmodifikationenRepraesentation,
      ableitungsModifikationenStrutkur: schema.ableitungsmodifikationenStruktur,
      documentSteckbriefFimId: DEFAULT_DOCUMENT_PROFILE,
      basicData: convertBaseData(schema),
      children: convertVersionChildren(schema.elemente),
      rules: convertRuleVersionReferences(schema.regeln),
    };
  }
}

function convertGroup(group: xdf2.Datenfeldgruppe): Group {
  const [fimId, fimVersion] = upgradeIdentifier(group.identifier);

  if (fimVersion === undefined) {
    return {
      type: "arbeitskopie",
      fimId,
      basicData: convertBaseData(group),
      art: undefined,
      children: convertChildren(group.elemente),
      rules: convertRuleReferences(group.regeln),
    };
  } else {
    return {
      type: "version",
      url: `https://fimportal.de/groups/baukasten/${group.id}/${group.version}`,
      namespace: "baukasten",
      fimId,
      fimVersion,
      basicData: convertBaseData(group),
      art: undefined,
      children: convertVersionChildren(group.elemente),
      rules: convertRuleVersionReferences(group.regeln),
    };
  }
}

function convertXdfStatus(
  status: xdf2.ElementStatus,
  releaseDate?: Date,
): FreigabeStatus {
  switch (status) {
    case "aktiv":
      return releaseDate
        ? FreigabeStatus.FachlichFreigegebenGold
        : FreigabeStatus.InBearbeitung;
    case "inVorbereitung":
      return FreigabeStatus.InBearbeitung;
    case "inaktiv":
      return FreigabeStatus.Inaktiv;
  }
}
function convertBaseData(data: xdf2.BaseData): BasicData {
  return {
    name: data.name,
    beschreibung: data.beschreibung,
    definition: data.definition,
    normReferences: data.bezug ? [{ value: data.bezug }] : [],
    releaseStatus: convertXdfStatus(data.status, data.freigabedatum),
    statusGesetztAm: data.freigabedatum,
    statusGesetztDurch: data.fachlicherErsteller,
    gueltigAb: data.gueltigAb,
    gueltigBis: data.gueltigBis,
    versionshinweis: data.versionshinweis,
    veroeffentlichungsdatum: data.veroeffentlichungsdatum,
    relations: [],
    keywords: [],
  };
}

function convertChildren(children: xdf2.ElementReference[]): Child[] {
  return children.map((ref) => {
    const [fimId, fimVersion] = upgradeIdentifier(ref.identifier);

    return {
      type: getChildType(ref.type),
      element: {
        fimId,
        fimVersion,
      },
      cardinality: ref.anzahl,
    };
  });
}

function convertVersionChildren(
  children: xdf2.ElementReference[],
): VersionChild[] {
  return children.map((ref) => {
    const [fimId, fimVersion] = upgradeIdentifier(ref.identifier);
    if (fimVersion === undefined) {
      throw new Error(`Expected versioned child reference: ${ref.identifier}`);
    }

    return {
      type: getChildType(ref.type),
      element: {
        fimId,
        fimVersion,
      },
      cardinality: ref.anzahl,
    };
  });
}

function getChildType(childType: "dataField" | "dataGroup"): "field" | "group" {
  switch (childType) {
    case "dataField":
      return "field";
    case "dataGroup":
      return "group";
  }
}

function upgradeIdentifier(identifier: string): [string, string | undefined] {
  // TODO: Make return-version optional
  const [fimId, fimVersion] = identifier.split(":");

  return upgradeXdf2Identifier(fimId, fimVersion);
}

export function upgradeXdf2Identifier(
  fimId: string,
  fimVersion: string,
): [string, string] {
  if (fimId.length === 9) {
    const prefix = fimId.substring(0, 3);
    const suffix = fimId.substring(3);
    fimId = `${prefix}000${suffix}`;
  } else {
    assert(fimId.length === 12, `Invalid fimId: ${fimId}`);
  }

  const versionParts = fimVersion.split(".");
  if (versionParts.length === 2) {
    fimVersion = `${versionParts[0]}.${versionParts[1]}.0`;
  } else {
    assert(versionParts.length === 1, `Invalid fimVersion: ${fimVersion}`);
  }

  return [fimId, fimVersion];
}

/**
 * Downgrades a xdf3 id and version to xdf2.
 * This is only donw temporarily, so that out editor can handle the legacy xdf2 data,
 * until actual xdf3 data is available.
 */
export function downgradeXdf3Identifier(
  fimId: string,
  fimVersion: string,
): [string, string] {
  if (fimId.length === 12) {
    const prefix = fimId.substring(0, 3);
    const suffix = fimId.substring(6);
    fimId = prefix + suffix;
  } else {
    assert(fimId.length === 9, `Invalid fim ID: ${fimId}`);
  }

  const versionParts = fimVersion.split(".");
  if (versionParts.length === 3) {
    fimVersion = `${versionParts[0]}.${versionParts[1]}`;
  } else {
    assert(versionParts.length === 2, `Invalid version: ${fimVersion}`);
  }

  return [fimId, fimVersion];
}
